import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { FlexContainer, ModalButtonWithForm } from 'components/common';
import {
  FormGroup,
  MultipleAttachments,
  TextArea,
  UserSelector
} from 'components/common/form';
import { UserPasswordReview } from 'components/users';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';
import { AchievementImportJson } from '.';

import type { Attachments, User } from 'shared-types';

export type FormValues = {
  [key: `userAttributes-${string}`]: string | number | boolean;
  comments: string;
  emails: string[];
  expirationInDays: number[];
  passwords: string[];
  usernames: string[];
  userSelector: 'emails' | 'usernames';
};

type PasswordReview = Record<string, string>;

type Props = {
  updateGroups: () => Promise<void>;
  user?: Pick<User, 'email' | 'username'>;
} & (
  | { isSupervisorPage: true; supervisedUserIds: number[] }
  | { isSupervisorPage?: false; supervisedUserIds?: undefined }
);

const AchievementGroupOfUsersAdd = ({
  isSupervisorPage = false,
  supervisedUserIds,
  updateGroups,
  user
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { achievement },
    user: loggedInUser,
    theme
  } = useReduxState();

  const uploader = useRef<() => Promise<Attachments>>(undefined);

  const [isVisible, setIsVisible] = useState(false);
  const [userPasswords, setUserPasswords] = useState<PasswordReview>({});

  const handlePasswordReviewHide = () => setUserPasswords({});

  const submit = async (values: FormValues) => {
    try {
      if (!values.userSelector) values.userSelector = 'emails';

      const attachments = await uploader.current();

      const groupAttributes = {};
      Object.keys(values).forEach((fieldKey) => {
        if (fieldKey.startsWith('userAttributes-')) {
          const attributeId = fieldKey.split('-')[1];
          groupAttributes[attributeId] = values[fieldKey];
        }
      });

      const users = values[values.userSelector];

      const groupMembers = [];
      for (let i = 0; i < users.length; i++) {
        const newUser = {
          ...(values.userSelector === 'emails'
            ? { email: users[i] }
            : { username: users[i] }),
          ...(values.passwords?.[i] && {
            password: values.passwords[i],
            expirationInDays: values.expirationInDays[i]
          })
        };

        groupMembers.push(newUser);
      }

      const response = await api.post(
        `/admin/achievements/${achievement.id}/group-completions`,
        {
          groupData: {
            attachments,
            comments: values.comments,
            status: 'not started',
            userAttributes: groupAttributes
          },
          groupMembers
        }
      );

      await updateGroups();

      const successMessage =
        `${messages.achievementGroupConversationAddSuccess}, ` +
        `${response.data.newUsers} ${messages.settingsAddUsersAccountsCreated}`;
      dispatch.addNotice({ text: successMessage, type: 'success' });

      if (values.usernames?.length && response.data.newUsers) {
        const userPasswords: Record<string, string> = {};
        values.passwords.forEach((password, i) => {
          if (password) userPasswords[values.usernames[i]] = password;
        });

        setUserPasswords(userPasswords);
      }
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  const initialValues = {
    emails: user?.email ? [user.email] : [],
    usernames: user?.username ? [user.username] : [],
    ...(user && user.username && !user.email
      ? { userSelector: 'usernames' }
      : {})
  };

  const isVuoadmin = loggedInUser?.roles.includes('vuoadmin');

  return (
    <FlexContainer>
      <ModalButtonWithForm
        buttonIcon="plus"
        buttonSize="header"
        buttonTextHideBreakpoint={breakpoints.medium - 1}
        contentStyle={{ padding: 0 }}
        initialValues={initialValues}
        modalActionText={messages.commonAdd}
        modalTitle={messages.commonAddParticipants}
        modalWidth="wide"
        onHide={() => setIsVisible(false)}
        onShow={() => setIsVisible(true)}
        onSubmit={submit}
        {...(user
          ? {
              buttonCategory: 'lighter',
              buttonProps: { tooltip: messages.commonAddParticipants }
            }
          : {
              buttonCategory: 'success',
              buttonText: messages.commonAddParticipants
            })}
        {...(isVuoadmin
          ? {
              buttonStyle: {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                marginRight: 1
              }
            }
          : {})}
      >
        {({ values }) =>
          isVisible ? (
            <>
              <div style={{ padding: 15 }}>
                <UserSelector
                  {...(isSupervisorPage
                    ? {
                        existingUsers: supervisedUserIds,
                        existingUsersOnly: true,
                        preventNewUsers: true
                      }
                    : {})}
                  {...(user && user.username && !user.email
                    ? { enforceUsername: true }
                    : {})}
                />
              </div>

              {values.emails?.length || values.usernames?.length ? (
                <div className="header">
                  <div className="options">
                    <FormGroup
                      label={messages.commonComments}
                      style={{ width: 250 }}
                    >
                      <TextArea field="comments" id="comments" rows={3} />
                    </FormGroup>

                    <FormGroup
                      label={messages.commonAttachments}
                      style={{ margin: 0, maxWidth: 250 }}
                    >
                      <MultipleAttachments
                        getUploadHandler={(upl) => (uploader.current = upl)}
                        useUnwrapped={true}
                      />
                    </FormGroup>

                    {achievement?.userAttributes?.map((attribute) => (
                      <FormGroup
                        key={attribute.id}
                        label={
                          attribute.titleTranslations?.[language] ||
                          attribute.title
                        }
                        style={{ margin: 0, maxWidth: 250 }}
                      >
                        <ProductUserCustomAttributeRenderFormField
                          attribute={attribute}
                        />
                      </FormGroup>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : null
        }
      </ModalButtonWithForm>

      {isVuoadmin ? <AchievementImportJson updateData={updateGroups} /> : null}

      <UserPasswordReview
        onModalDismiss={handlePasswordReviewHide}
        passwords={userPasswords}
      />

      <style jsx>{`
        .header {
          border-top: 1px solid ${theme.lightGreyDark};
          padding: 15px 15px 20px;
        }
        .options {
          align-items: flex-start;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px;
          width: 100%;
        }
      `}</style>
    </FlexContainer>
  );
};

export default AchievementGroupOfUsersAdd;
